import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { UploadService } from '../../services/upload.service';
import { HttpEventType } from '@angular/common/http';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LanguageService } from '../../services/language.service';
import { Language } from '../../models/language.model';
import { LucideAngularModule } from 'lucide-angular';
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import { languagesMustDifferValidator } from '../../validators/languages-must-different';
import { CommonModule, NgStyle } from '@angular/common';
import { Store } from '@ngrx/store';
import { CreateJobResponse } from '../../models/create-job-response.model';
import { Job } from '../../class/job.class';
import { JobModel } from '../../models/job.model';
import { JobsActions } from '../../store/jobs/jobs.actions';
import { ProductsActions } from '../../store/products/products.actions';
import { ResponsiveService } from '../../services/reponsive.service';
import { LottieComponent } from 'ngx-lottie';
import { selectLanguageById } from '../../store/languages/languages.selects';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../boutons/button/button.component';
import { ClientActions } from '../../store/client/client.actions';
import {
  selectClientIsSubscribed,
  selectClientTokensLeft,
} from '../../store/client/client.selects';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { TokensPack } from '../../class/tokensPack.class';
import { TokensPacksModalComponent } from '../modals/tokens-packs-modal/tokens-packs-modal.component';
import { StripeCheckoutService } from '../../services/stripe-checkout.service';
import { FileDropDirective } from '../../directives/file-drop.directive';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-upload',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    LottieComponent,
    TranslateModule,
    LucideAngularModule,
    CommonModule,
    NgStyle,
    FileDropDirective,
  ],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
})
export class UploadComponent implements OnInit, OnDestroy {
  private uploadService: UploadService = inject(UploadService);
  private languageService: LanguageService = inject(LanguageService);
  private store: Store = inject(Store);
  private responsiveService: ResponsiveService = inject(ResponsiveService);
  private stripeCheckoutService: StripeCheckoutService = inject(
    StripeCheckoutService,
  );
  private dialog: Dialog = inject(Dialog);

  videoPlayerRef: Signal<ElementRef<HTMLVideoElement>> = viewChild.required(
    'videoPlayer',
    { read: ElementRef<HTMLVideoElement> },
  );

  complete: OutputEmitterRef<number | null> = output();
  isDesktop: Signal<boolean> = toSignal(this.responsiveService.desktop$, {
    initialValue: false,
  });
  isLoading: WritableSignal<boolean> = signal(false);
  uploadProgress: WritableSignal<number> = signal(0);
  uploadError: WritableSignal<string | null> = signal(null);

  clientTokenLeft: Signal<number> = toSignal(
    this.store.select(selectClientTokensLeft),
    { initialValue: 0 },
  );

  clientIsSubscribed: Signal<boolean> = toSignal(
    this.store.select(selectClientIsSubscribed),
    { initialValue: false },
  );


  subscriptions: Subscription[] = [];
  uploadForm: FormGroup = new FormGroup(
    {
      upload: new FormControl(null, Validators.required),
      title: new FormControl<string | null>(
        null,
        Validators.compose([
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(40),
        ]),
      ),
      context: new FormControl<string | null>(null),
      languageTo: new FormControl<number | null>(null, [Validators.required]),
      languageFrom: new FormControl<number | null>(null, [Validators.required]),
    },
    { validators: [languagesMustDifferValidator] },
  );

  languages: Language[] = [];
  defaultOriginalLanguageId: number | undefined;
  defaultOutputLanguageId: number | undefined;
  uploadResponse: any = undefined;
  uploadVideoFormat: string = '';
  previewUrl: string | null = null;
  fileSize: number | null = null;
  videoDuration: number = 0; // durée de la video en secondes
  outpoutLanguages: Language[] = [];

  openTokensPackModal() {
    this.store.dispatch(ProductsActions.loadProducts());
    const dialogRef: DialogRef<unknown, TokensPacksModalComponent> =
      this.dialog.open(TokensPacksModalComponent, {
        width: '500px',
      });
    const tokensPacksModalComponent: TokensPacksModalComponent =
      dialogRef.componentInstance as TokensPacksModalComponent;
    tokensPacksModalComponent.tokensPackClick.subscribe(
      (tokenPack: TokensPack) => {
        this.stripeCheckoutService.checkoutTokensPack(tokenPack.priceId);
        dialogRef.close();
      },
    );
  }

  onFileSelected(event: Event) {
    this.isLoading.set(true);
    const input: HTMLInputElement = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.fileSize = null;
      }

      const file = input.files[0];
      this.uploadForm.get('upload')?.setValue(file);
      this.uploadVideoFormat = file.type;
      this.previewUrl = URL.createObjectURL(file);
      this.fileSize = file.size / Math.pow(1024, 3);

      if (this.uploadForm.controls['title'].value === null) {
        this.uploadForm.controls['title'].setValue(file.name);
        this.uploadForm.controls['title'].markAsTouched();
      }

      setTimeout(() => {
        this.isLoading.set(false);
        requestAnimationFrame(() => {
          const videoElement = this.videoPlayerRef().nativeElement;
          if (!videoElement) {
            console.error('Élément vidéo non trouvé');
            return;
          }
          videoElement.load();
          videoElement.onloadedmetadata = () => {
            this.videoDuration = Math.floor(videoElement.duration);
          };
        });
      });
    }
  }

  onSubmit() {
    this.isLoading.set(true);
    const file: File = this.uploadForm.get('upload')?.value;
    if (!file) {
      console.error('Aucun fichier sélectionné');
      this.complete.emit(null);
      this.isLoading.set(false);
      return;
    }
    this.uploadProgress.set(0);
    this.uploadError.set(null);
    this.subscriptions.push(
      this.uploadService.uploadVideo(file).subscribe({
        next: (uploadResponse: any) => {
          if (uploadResponse.type === HttpEventType.UploadProgress) {
            const percent = uploadResponse.total
              ? Math.round((100 * uploadResponse.loaded) / uploadResponse.total)
              : 0;
            this.uploadProgress.set(percent);
            return;
          }
          if (uploadResponse.body) {
            const body: any = uploadResponse.body;
            this.subscriptions.push(
              this.uploadService
                .sendData(
                  body.id,
                  this.uploadForm.controls['title'].value,
                  this.uploadForm.controls['context'].value,
                  this.videoDuration,
                  this.uploadForm.controls['languageFrom'].value,
                  this.uploadForm.controls['languageTo'].value,
                )
                .subscribe({
                  next: (dataResponse: CreateJobResponse) => {
                    const safeDate = (value: any): Date => {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? new Date() : date;
                    };
                    const newJobModel: JobModel = {
                      id: dataResponse.job.id,
                      user: dataResponse.job.user,
                      title: dataResponse.job.title,
                      context: dataResponse.job.context,
                      file_extension: dataResponse.job.file_extension,
                      status: dataResponse.job.status,
                      duration: this.videoDuration,
                      thumbnailUrl: dataResponse.job.thumbnailUrl,
                      lang_source: {
                        id: dataResponse.job.lang_source,
                        name: '',
                        short: '',
                        flag_code: '',
                      },
                      lang_cible: {
                        id: dataResponse.job.lang_cible,
                        name: '',
                        short: '',
                        flag_code: '',
                      },
                      tasks: [...dataResponse.tasks],
                      created_at: safeDate(dataResponse.job.created_at),
                      updated_at: safeDate(dataResponse.job.updated_at),
                      deleted_at: safeDate(dataResponse.job.deleted_at),
                    };
                    forkJoin([
                      this.store
                        .select(
                          selectLanguageById(dataResponse.job.lang_source),
                        )
                        .pipe(take(1)),
                      this.store
                        .select(selectLanguageById(dataResponse.job.lang_cible))
                        .pipe(take(1)),
                    ] as [
                        Observable<Language | undefined>,
                        Observable<Language | undefined>,
                      ]).subscribe({
                        next: ([lang_source, lang_cible]: [
                          Language | undefined,
                          Language | undefined,
                        ]) => {
                          const newJob: Job = new Job({
                            ...newJobModel,
                            lang_cible: lang_cible || {
                              id: dataResponse.job.lang_source,
                              name: '',
                              short: '',
                              flag_code: '',
                            },
                            lang_source: lang_source || {
                              id: dataResponse.job.lang_source,
                              name: '',
                              short: '',
                              flag_code: '',
                            },
                          });
                          this.store.dispatch(
                            JobsActions.addJob({ job: newJob }),
                          );
                          this.store.dispatch(
                            ClientActions.setTokens({
                              tokens: dataResponse.token_left,
                            }),
                          );
                          this.complete.emit(dataResponse.job.id);
                          this.isLoading.set(false);
                        },
                      });
                  },
                  error: (error: Error) => {
                    console.error(error);
                    this.uploadError.set("Une erreur s'est produite lors de la configuration du job. Veuillez réessayer.");
                    this.isLoading.set(false);
                  },
                  complete: () => {
                    this.uploadResponse = undefined;
                    this.isLoading.set(false);
                  },
                }),
            );
          }
        },
        error: (error) => {
          console.error("Échec de l'upload", error);
          this.uploadError.set("L'envoi du fichier a échoué. Vérifiez votre connexion et réessayez.");
          this.isLoading.set(false);
        },
      }),
    );
  }

  removeVideo() {
    this.uploadForm.get('upload')?.setValue(null);
    this.previewUrl && URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
    this.fileSize = null;
  }

  onFileDropped(file: File) {
    if (file.type === 'video/mp4') {
      this.processFile(file);
    } else {
      console.error('Seuls les fichiers MP4 sont acceptés');
    }
  }

  private processFile(file: File) {
    this.isLoading.set(true);

    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.fileSize = null;
    }

    this.uploadForm.get('upload')?.setValue(file);
    this.uploadVideoFormat = file.type;
    this.previewUrl = URL.createObjectURL(file);
    this.fileSize = file.size / Math.pow(1024, 3);

    if (this.uploadForm.controls['title'].value === null) {
      this.uploadForm.controls['title'].setValue(file.name);
      this.uploadForm.controls['title'].markAsTouched();
    }

    setTimeout(() => {
      this.isLoading.set(false);
      requestAnimationFrame(() => {
        const videoElement = this.videoPlayerRef().nativeElement;
        if (!videoElement) {
          console.error('Élément vidéo non trouvé');
          return;
        }
        videoElement.load();
        videoElement.onloadedmetadata = () => {
          this.videoDuration = Math.floor(videoElement.duration);
        };
      });
    });
  }

  ngOnInit(): void {
    const languageSub = this.languageService.getLanguages().subscribe({
      next: (languages: Language[]) => {
        this.languages = languages;
        this.defaultOriginalLanguageId =
          languages.find((lang) => lang.short === 'fr')?.id || 1;
        this.defaultOutputLanguageId =
          languages.find((lang) => lang.short === 'en')?.id || 2;
        this.uploadForm.patchValue({
          languageFrom: this.defaultOriginalLanguageId,
          languageTo: this.defaultOutputLanguageId,
        });
      },
      error: (err: Error) => {
        console.error('Erreur lors de la récupération des langues :', err);
      },
    });

    this.subscriptions.push(languageSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
